// src/types/staff.ts
// FIXED VERSION: Properly includes 'both' semester allowances in calculations

// Allowance template for preset allowance types
export interface AllowanceTemplate {
  id: string;
  name: string;
  defaultMinutes: number;
  description?: string;
  /**
   * Whether this template is offered when adding a new allowance.
   *
   * The `is_active` column has always existed on allowance_templates but was
   * dropped on the way through DataContext, so retiring a template did nothing —
   * duplicates such as two "POL 1" rows and a "Year 8 Learning Leader " with a
   * trailing space stayed in the picker with no way to tell them apart.
   *
   * Inactive templates are still FETCHED, deliberately. Existing allowances
   * reference a template by id and the edit form looks it up; hiding it from the
   * list is right, dropping it from the data would break editing every allowance
   * that uses it.
   */
  isActive?: boolean;
}

// Staff allowance instance
export interface StaffAllowance {
  id: string;
  templateId: string;
  name: string;
  minutes: number;
  description?: string;
  appliesTo: 'semester1' | 'semester2' | 'both';
  /**
   * Academic year this allowance applies to.
   *
   * Optional for backward compatibility only. An allowance with no year is
   * treated as applying to EVERY year (see allowanceAppliesToYear), matching the
   * "null years are kept, not dropped" rule already used for grids — a record
   * that predates this field must never silently vanish from every total at
   * once. Everything written since v2.11.0 carries a year, and the 211 records
   * that predate it were backfilled by
   * 20260910000000_backfill_allowance_years.sql.
   */
  year?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

// Semester-specific data for each staff member
export interface StaffSemesterData {
  allowances: StaffAllowance[];
  baseMinutesPerFortnight: number; // Default 2220 for full-time
  adjustedMinutesPerFortnight?: number; // After FTE adjustment
  allocatedMinutes: number; // Minutes assigned in grid
  availableMinutes: number; // Adjusted minutes minus allowances
  utilization: number; // Percentage of available minutes allocated
}

// Default allowance templates
export const DEFAULT_ALLOWANCE_TEMPLATES: AllowanceTemplate[] = [{
  id: 'pol1',
  name: 'POL 1',
  defaultMinutes: 30,
  description: 'Position of Leadership Level 1'
}, {
  id: 'pol2',
  name: 'POL 2',
  defaultMinutes: 60,
  description: 'Position of Leadership Level 2'
}, {
  id: 'dept_head',
  name: 'Department Head',
  defaultMinutes: 80,
  description: 'Head of academic department'
}, {
  id: 'year_coord',
  name: 'Year Level Coordinator',
  defaultMinutes: 40,
  description: 'Year level coordination responsibilities'
}, {
  id: 'curr_leader',
  name: 'Curriculum Leader',
  defaultMinutes: 60,
  description: 'Curriculum development and leadership'
}, {
  id: 'prof_dev',
  name: 'Professional Development',
  defaultMinutes: 20,
  description: 'Time allocated for professional development'
}, {
  id: 'mentoring',
  name: 'Mentoring',
  defaultMinutes: 40,
  description: 'Mentoring other staff members'
}, {
  id: 'special_project',
  name: 'Special Project',
  defaultMinutes: 0,
  description: 'Custom project allocation'
}];

// FIXED: Helper function to calculate semester allowance minutes including 'both'
export const calculateSemesterAllowanceMinutes = (allowances: StaffAllowance[], semester: 'semester1' | 'semester2', bothAllowances?: StaffAllowance[], year?: number): number => {
  let totalMinutes = 0;

  // Add semester-specific allowances
  if (allowances && Array.isArray(allowances)) {
    totalMinutes += allowances.reduce((total, allowance) => {
      if (!allowanceAppliesToYear(allowance, year)) return total;
      if (allowance.appliesTo === semester || allowance.appliesTo === 'both') {
        return total + allowance.minutes;
      }
      return total;
    }, 0);
  }

  // Add 'both' semester allowances if provided
  if (bothAllowances && Array.isArray(bothAllowances)) {
    totalMinutes += bothAllowances.reduce((total, allowance) => {
      if (!allowanceAppliesToYear(allowance, year)) return total;
      return total + allowance.minutes;
    }, 0);
  }
  return totalMinutes;
};
export const calculateAvailableMinutes = (baseMinutes: number, fte: number, allowanceMinutes: number): number => {
  return Math.max(0, Math.round(baseMinutes * fte) - allowanceMinutes);
};
export const calculateUtilization = (allocatedMinutes: number, availableMinutes: number): number => {
  if (availableMinutes <= 0) return allocatedMinutes > 0 ? 100 : 0;
  return Math.round(allocatedMinutes / availableMinutes * 100);
};

// FIXED: Calculate staff semester load including 'both' allowances
export const calculateStaffSemesterLoad = (staff: any, semester: 'semester1' | 'semester2', grid: any, lines: any[]): StaffSemesterData => {
  // Default values
  const baseMinutesPerFortnight = staff.baseMinutesPerFortnight || 2220;
  const fte = staff.fte || 1.0;

  // Get semester-specific allowances
  const semesterAllowances = staff[semester]?.allowances || [];

  // Get 'both' semester allowances
  const bothAllowances = staff.both?.allowances || [];

  // FIXED: Calculate total allowance minutes including 'both'
  const allowanceMinutes = calculateSemesterAllowanceMinutes(semesterAllowances, semester, bothAllowances);

  // Calculate adjusted minutes (base × FTE)
  const adjustedMinutesPerFortnight = Math.round(baseMinutesPerFortnight * fte);

  // Calculate available minutes (adjusted - allowances)
  const availableMinutes = calculateAvailableMinutes(baseMinutesPerFortnight, fte, allowanceMinutes);

  // Get semester line IDs
  const semesterLineIds = lines.filter(line => line.studyPeriod === semester).map(line => line.id);

  // Calculate allocated minutes from grid assignments
  let allocatedMinutes = 0;
  if (grid && grid.classes) {
    grid.classes.forEach(classItem => {
      Object.entries(classItem.lineAssignments || {}).forEach(([lineId, teacherCode]) => {
        if (teacherCode === staff.initial && semesterLineIds.includes(lineId)) {
          // Each line is 440 minutes per fortnight
          allocatedMinutes += 440;
        }
      });
    });
  }

  // Calculate utilization
  const utilization = calculateUtilization(allocatedMinutes, availableMinutes);

  // Combine all allowances for display (semester-specific + both)
  const allAllowances = [...semesterAllowances, ...bothAllowances];
  return {
    allowances: allAllowances,
    baseMinutesPerFortnight,
    adjustedMinutesPerFortnight,
    allocatedMinutes,
    availableMinutes,
    utilization
  };
};

/**
 * Does this allowance count towards the given academic year?
 *
 * Allowances are stored per staff member in staff.semester1/semester2/both, so
 * before v2.11.0 there was exactly one set per person for all time: entering a
 * 2027 allowance overwrote the 2026 one in place. Every read path must now filter
 * by year, or a teacher's 2026 and 2027 allowances both count against whichever
 * year is on screen.
 *
 * A year-less allowance matches every year — see StaffAllowance.year.
 */
export const allowanceAppliesToYear = (allowance: StaffAllowance, year?: number): boolean => {
  if (year == null) return true;      // caller does not care about year
  if (allowance.year == null) return true;  // pre-v2.11.0 record: applies to all years
  return allowance.year === year;
};

// Helper function to get all allowances for a staff member in a semester
export const getAllowancesForSemester = (staff: any, semester: 'semester1' | 'semester2', year?: number): StaffAllowance[] => {
  const semesterAllowances = (staff[semester]?.allowances || []).filter((a: StaffAllowance) => allowanceAppliesToYear(a, year));
  const bothAllowances = (staff.both?.allowances || []).filter((a: StaffAllowance) => allowanceAppliesToYear(a, year));
  return [...semesterAllowances, ...bothAllowances];
};

// Helper function to calculate total allowance minutes for display (with pro-rata calculation based on FTE)
export const getTotalAllowanceMinutes = (staff: any, semester: 'semester1' | 'semester2', year?: number): number => {
  const semesterAllowances = (staff[semester]?.allowances || []).filter((a: StaffAllowance) => allowanceAppliesToYear(a, year));
  const bothAllowances = (staff.both?.allowances || []).filter((a: StaffAllowance) => allowanceAppliesToYear(a, year));
  const staffFTE = staff.fte || 1.0; // Default to 1.0 if FTE not set
  let total = 0;

  // Add semester-specific allowances with pro-rata calculation
  total += semesterAllowances.reduce((sum: number, a: StaffAllowance) => {
    if (a.appliesTo === semester || a.appliesTo === 'both') {
      // Apply pro-rata calculation: allowance minutes * staff FTE
      return sum + Math.round(a.minutes * staffFTE);
    }
    return sum;
  }, 0);

  // Add 'both' semester allowances with pro-rata calculation
  total += bothAllowances.reduce((sum: number, a: StaffAllowance) => {
    // Apply pro-rata calculation: allowance minutes * staff FTE
    return sum + Math.round(a.minutes * staffFTE);
  }, 0);
  return total;
};

/**
 * FTE as stored → FTE the app works with. Only a MISSING value falls back to
 * full time. A stored 0 is a real 0 (e.g. on leave): `fte || 1.0` used to turn
 * it into 1.0, and such staff got full-time load letters (#288).
 */
export const normaliseFte = (fte: number | string | null | undefined): number => {
  if (fte === null || fte === undefined || fte === '') return 1.0;
  const n = Number(fte);
  return Number.isFinite(n) ? n : 1.0;
};

// Helper function to calculate pro-rata allowance minutes for a single allowance
export const getEffectiveAllowanceMinutes =(allowanceMinutes: number, staffFTE: number): number => {
  return Math.round(allowanceMinutes * staffFTE);
};

// Helper function to get all allowances with their effective (pro-rata) minutes
export const getAllowancesWithEffectiveMinutes = (staff: any, semester: 'semester1' | 'semester2', year?: number): Array<StaffAllowance & { effectiveMinutes: number }> => {
  const semesterAllowances = (staff[semester]?.allowances || []).filter((a: StaffAllowance) => allowanceAppliesToYear(a, year));
  const bothAllowances = (staff.both?.allowances || []).filter((a: StaffAllowance) => allowanceAppliesToYear(a, year));
  const staffFTE = staff.fte || 1.0;
  
  const result: Array<StaffAllowance & { effectiveMinutes: number }> = [];
  
  // Process semester-specific allowances
  semesterAllowances.forEach((allowance: StaffAllowance) => {
    if (allowance.appliesTo === semester || allowance.appliesTo === 'both') {
      result.push({
        ...allowance,
        effectiveMinutes: getEffectiveAllowanceMinutes(allowance.minutes, staffFTE)
      });
    }
  });
  
  // Process 'both' semester allowances
  bothAllowances.forEach((allowance: StaffAllowance) => {
    result.push({
      ...allowance,
      effectiveMinutes: getEffectiveAllowanceMinutes(allowance.minutes, staffFTE)
    });
  });
  
  return result;
};